import { ErrorMessage, Field, useFormikContext } from "formik";
import { CardBody, CardHeader, Col, FormGroup, Row } from "reactstrap";
import organizationData from "../../../utils/organizationData.json";
import { useEffect, useState } from "react";
import { OrganizationFormData } from "../../../types/organization/OrganizationTypes";
import UploadFile from "./UploadFile";
import Select from "react-select";
import { customReactSelectStyles } from "../../../styles/ReactSelectStyles";
import { useTranslation } from "react-i18next";
import { switchSaveOption } from "../../../data/forms/forms-control/forms-widgets/switch/Switch";
import VariationOfSwitchOne from "../../forms/forms-widgets/switch/variation-of-switches/VariationOfSwitchOne";
import { formatOptionLabelWithFlag } from "../../users/organization-admin/edit-user-list/UserListInfoSection";

type OptionType = {
  value: string;
  label: string;
  flag?: string;
  symbol?: string;
};



interface OrganizationDetailsSectionProps {
  isEdit?: boolean;
}

const OrganizationDetailsSection = ({
  isEdit,
}: OrganizationDetailsSectionProps) => {
  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext<OrganizationFormData>();
  const [countryOptions, setCountryOptions] = useState<OptionType[]>([]);
  const [cityOptions, setCityOptions] = useState<OptionType[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<OptionType | null>(
    null
  );
  const [selectedCity, setSelectedCity] = useState<OptionType | null>(null);

  useEffect(() => {
    const countries = organizationData.countries.map((country) => ({
      value: country.value,
      label: country.label,
      flag: country.flag,
    }));
    setCountryOptions(countries);
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const cities =
        organizationData.cities[
          selectedCountry.value as keyof typeof organizationData.cities
        ] || [];
      setCityOptions(cities);
    } else {
      setCityOptions([]);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (isEdit && countryOptions.length > 0 && values.country) {
      const country = countryOptions.find((opt) => opt.value === values.country);
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [isEdit, values.country, countryOptions]);

  useEffect(() => {
    if (isEdit && cityOptions.length > 0 && values.city) {
      const city = cityOptions.find((opt) => opt.value === values.city);
      if (city) {
        setSelectedCity(city);
      }
    }
  }, [isEdit, values.city, cityOptions]);



  return (
    <div className="mb-4">
      <CardHeader>
        <h5>{t("OrganizationDetails")}</h5>
      </CardHeader>
      <CardBody>
        <Row>
          <Col sm="4">
            <FormGroup>
              <label>
                {t("OrganizationName")}
                <span className="txt-danger">*</span>
              </label>
              <Field
                name="organizationName"
                type="text"
                className="form-control"
                placeholder={t("OrganizationNamePlaceholder")}
              />
              <ErrorMessage
                name="organizationName"
                component="span"
                className="text-danger"
              />
            </FormGroup>
          </Col>

          <Col sm="4">
            <FormGroup>
              <label>
                {t("OrganizationCountry")} <span className="txt-danger">*</span>
              </label>
              <Select
                name="country"
                options={countryOptions}
                value={selectedCountry}
                onChange={(option) => {
                  setSelectedCountry(option);
                  setSelectedCity(null);
                  setFieldValue("country", option ? option.value : "");
                  setFieldValue("city", "");
                }}
                formatOptionLabel={formatOptionLabelWithFlag}
                styles={customReactSelectStyles}
                placeholder={t("SelectCountry")}
                isClearable
                 classNamePrefix="select"
              />
              <ErrorMessage
                name="country"
                component="span"
                className="text-danger"
              />
            </FormGroup>
          </Col>

          <Col sm="4">
            <FormGroup>
              <label>
                {t("OrganizationCity")} <span className="txt-danger">*</span>
              </label>
              <Select
                name="city"
                options={cityOptions}
                value={selectedCity}
                onChange={(option) => {
                  setSelectedCity(option);
                  setFieldValue("city", option ? option.value : "");
                }}
                styles={customReactSelectStyles}
                placeholder={t("OrganizationCityPlaceholder")}
                isDisabled={!selectedCountry}
                isClearable
                classNamePrefix="select"
              />
              <ErrorMessage
                name="city"
                component="span"
                className="text-danger"
              />
            </FormGroup>
          </Col>

          <Col sm="4">
            <FormGroup>
              <label>{t("OrganizationIndustry")}</label>
             
               <Select
                name="industry"
                options={organizationData.industries}
                placeholder={t("OrganizationIndustryPlaceholder")}
                className="basic-single-select"
                classNamePrefix="select"
                isSearchable={true}
                styles={customReactSelectStyles}
                value={organizationData.industries.find(
                  (opt) => opt.value === values.industry
                ) || null}
                onChange={(selectedOption) => {
                  setFieldValue('industry', selectedOption?.value);
                }}
                isClearable
              />
              <ErrorMessage
                name="industry"
                component="span"
                className="text-danger"
              />
            </FormGroup>
          </Col>

          <Col sm="4">
            <FormGroup>
              <label>{t("Authority")}</label>
               <Select
                name="authority"
                options={organizationData.authorities}
                placeholder={t("AuthorityPlaceholder")}
                className="basic-single-select"
                classNamePrefix="select"
                isSearchable={true}
                styles={customReactSelectStyles}
                value={organizationData.authorities.find(
                  (opt) => opt.value === values.authority
                ) || null}
                onChange={(selectedOption) => {
                  setFieldValue('authority', selectedOption?.value);
                }}
                isClearable
              />
              <ErrorMessage
                name="authority"
                component="span"
                className="text-danger"
              />
            </FormGroup>
          </Col>

          <Col sm="4">
            <UploadFile label={t("OrganizationLogo")} />
          </Col>
          <Col sm="4">
            <FormGroup>
              <label>{t("OrganizationWebsite")}</label>
              <Field
                name="organizationWebsite"
                type="text"
                className="form-control"
                placeholder={t("OrganizationWebsitePlaceholder")}
              />
              <ErrorMessage
                name="organizationWebsite"
                component="span"
                className="text-danger"
              />
            </FormGroup>
          </Col>
          <Col sm="4">
            <FormGroup>
              <label>{t("OrganizationAddress")}</label>
              <Field
                name="address"
                as="textarea"
                className="form-control"
                placeholder={t("OrganizationAddressPlaceholder")}
                rows="2"
              />
              <ErrorMessage
                name="address"
                component="span"
                className="text-danger"
              />
            </FormGroup>
          </Col>

          <Col sm="4">
            <VariationOfSwitchOne switchData={switchSaveOption} />
          </Col>
        </Row>
      </CardBody>
    </div>
  );
};

export default OrganizationDetailsSection;